import loadable from '@loadable/component';
import React from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import FullscreenError from '@99designs/apps/spa/common/FullscreenError/FullscreenError';

import { useNavigationTracking } from '@99designs/apps/spa/common/Analytics/PageViewTracker';
import { Role } from '@99designs/frontend-components/apps/global-nav/consts';
import { Site } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import { NoSsr } from '@99designs/ui';
import { WorkPageStatus } from '@99designs/apps/spa/linkTo';
import { useIsExperimentActivated } from '@99designs/apps/spa/common/ExperimentsContext';

import Layout from './common/Layout/Layout';
import Loading from './common/Loading';
import { useViewer } from './common/Viewer/ViewerContext';
import { ViewerContext_UserFragment } from './common/Viewer/ViewerContext.generated';
import AuthenticatedRoute from './common/Routing/AuthenticatedRoute';

const Messages = loadable(() => import('./pages/messages'));
const WebOnboarderPage = loadable(() => import('./pages/web/get-started/WebOnboarderPage'));
const CancelInvitesPage = loadable(() => import('./pages/projects/cancel-invites'));
const ProjectsApp = loadable(() => import('./pages/projects/ProjectsApp'));
const AuthPageContainer = loadable(() => import('./pages/auth/PageContainer'));
const VerifyAddPayoutMethod = loadable(() => import('./pages/auth/VerifyAddPayoutMethod'));
const VerifyAddPayoutRequest = loadable(() => import('./pages/auth/VerifyAddPayoutRequest'));
const Notifications = loadable(() => import('./pages/notifications'));
const Status = loadable(() => import('./pages/status'));
const Account = loadable(() => import('./pages/account'));
const HandoverMessages = loadable(() => import('./pages/handover/messages'));
const Create = loadable(() => import('./pages/create'));
const YourProjectsApp = loadable(() => import('./pages/your-projects/YourProjectsApp'));
const YourWorkApp = loadable(() => import('./pages/your-work/YourWorkApp'));
const AdminRedirects = loadable(() => import('./pages/admin-redirects'));
const ProjectInvoicePage = loadable(() => import('./pages/projects/invoices/ProjectInvoicePage'));
const BrowseProjects = loadable(() => import('./pages/browse-projects/BrowseProjects'));
const GettingStarted = loadable(() => import('./pages/getting-started/GettingStartedPage'));
const CreatorApplication = loadable(() => import('./pages/onboarder/CreatorApplicationPage'));
const CancelProjectPage = loadable(() => import('./pages/projects/cancel-project'));
const SSOMismatchedUserPage = loadable(() => import('./pages/auth/SSO/SSOMismatchedUserPage'));
const NewUserPage = loadable(() => import('./pages/auth/SSO/NewUserPage'));
const LinkAccountsPage = loadable(() => import('./pages/auth/SSO/LinkAccountsPage'));
const VerifyLinkAccountsPage = loadable(() => import('./pages/auth/SSO/VerifyLinkAccountsPage'));
const LinkingSuccessPage = loadable(() => import('./pages/auth/SSO/LinkingSuccessPage'));

export default function Routes({ site }: { site: Site }) {
    const viewer = useViewer();
    useNavigationTracking();

    const onboardingExperimentActive = useIsExperimentActivated('onboardingWizard', 'enabled');

    return (
        <Switch>
            <Route path="/admin-redirect/*" render={() => <AdminRedirects />} />
            <Route
                exact
                path="/login"
                render={() => <AuthPageContainer fallback={<Loading />} initialPage="login" />}
            />
            <Route
                exact
                path="/signup/:variant(designer|invite)?"
                render={(props: RouteComponentProps<{ variant?: 'designer' | 'invite' }>) => (
                    <AuthPageContainer
                        fallback={<Loading />}
                        initialPage="signup"
                        variant={props.match.params.variant}
                    />
                )}
            />
            <Route
                exact
                path="/auth/verify"
                render={() => (
                    <AuthPageContainer fallback={<Loading />} initialPage="verifyLogin" />
                )}
            />
            <Route
                path="/auth/sso/new-user"
                render={(props: RouteComponentProps) => (
                    <NewUserPage fallback={<Loading />} {...props} />
                )}
            />
            <Route
                path="/auth/sso/link-accounts"
                render={(props: RouteComponentProps) => (
                    <LinkAccountsPage fallback={<Loading />} {...props} />
                )}
            />
            <Route
                path="/auth/sso/verify-link-accounts"
                render={(props: RouteComponentProps) => (
                    <VerifyLinkAccountsPage fallback={<Loading />} {...props} />
                )}
            />
            <Route
                path="/auth/sso/linking-success"
                render={(props: RouteComponentProps) => (
                    <LinkingSuccessPage fallback={<Loading />} {...props} />
                )}
            />
            <Route
                path="/auth/sso/mismatched-user"
                render={(props: RouteComponentProps) => (
                    <SSOMismatchedUserPage fallback={<Loading />} {...props} />
                )}
            />
            <Route
                path={['/briefs/*', '/squarespace/*']}
                render={() => (
                    <NoSsr fallback={<Loading />}>
                        <Create />
                    </NoSsr>
                )}
            />
            <Route path="/web/get-started" render={() => <WebOnboarderPage />} />
            <AuthenticatedRoute
                exact
                path="/work/:status(in-progress|all|completed|canceled)?"
                render={(props: RouteComponentProps<{ status?: WorkPageStatus }>) => (
                    <Layout role={primaryRole(viewer)} site={site} footerStyle="reduced">
                        <YourWorkApp
                            status={props.match.params.status}
                            language={site.getLanguageCode()}
                        />
                    </Layout>
                )}
            />
            <AuthenticatedRoute
                exact
                path="/spa/projects/dashboard/:status(active|drafts|cancelled|completed|invited|started)?"
                render={(
                    props: RouteComponentProps<{
                        status?:
                            | 'active'
                            | 'drafts'
                            | 'cancelled'
                            | 'completed'
                            | 'invited'
                            | 'started';
                    }>
                ) => (
                    <Layout role={primaryRole(viewer)} site={site} footerStyle="reduced">
                        <YourProjectsApp status={props.match.params.status} />
                    </Layout>
                )}
            />
            {onboardingExperimentActive && (
                <AuthenticatedRoute
                    path="/account/application"
                    render={() => (
                        <NoSsr fallback={<Loading />}>
                            <CreatorApplication role={primaryRole(viewer)} site={site} />
                        </NoSsr>
                    )}
                />
            )}
            <Route
                path={'/'}
                render={() => (
                    <Layout role={primaryRole(viewer)} site={site}>
                        <Switch>
                            <AuthenticatedRoute
                                path="/messages/:recipientId?"
                                render={(props: RouteComponentProps<{ recipientId: string }>) => (
                                    <Messages
                                        recipientId={props.match.params.recipientId}
                                        fallback={<Loading />}
                                        {...props}
                                    />
                                )}
                            />
                            <AuthenticatedRoute
                                path="/notifications"
                                render={(props: RouteComponentProps) => (
                                    <Notifications fallback={<Loading />} {...props} />
                                )}
                            />
                            <Route
                                path="/auth/verify-payout"
                                render={(props: RouteComponentProps) => (
                                    <VerifyAddPayoutMethod fallback={<Loading />} {...props} />
                                )}
                            />
                            <Route
                                path="/auth/verify-new-payout"
                                render={(props: RouteComponentProps) => (
                                    <VerifyAddPayoutRequest fallback={<Loading />} {...props} />
                                )}
                            />
                            <AuthenticatedRoute
                                path={['/projects/browse', '/projects/job/:jobId']}
                                render={() => (
                                    <NoSsr fallback={<Loading />}>
                                        <BrowseProjects />
                                    </NoSsr>
                                )}
                            />
                            <Route
                                exact
                                path="/projects/:projectId/cancel-invites"
                                component={CancelInvitesPage}
                            />
                            <AuthenticatedRoute
                                path={[
                                    '/spa/projects/:projectId/cancel',
                                    '/projects/:projectId/cancel',
                                    // TODO: /spa route will be used to handle existing requests, can be removed in few days
                                ]}
                                render={(props: RouteComponentProps<{ projectId: string }>) => (
                                    <CancelProjectPage projectId={props.match.params.projectId} />
                                )}
                            />
                            <Route
                                exact
                                path="/spa/projects/:projectId/invoices/:invoiceId"
                                render={(
                                    props: RouteComponentProps<{
                                        projectId: string;
                                        invoiceId: string;
                                    }>
                                ) => (
                                    <ProjectInvoicePage
                                        fallback={<Loading />}
                                        projectId={props.match.params.projectId}
                                        invoiceId={props.match.params.invoiceId}
                                        from={
                                            new URLSearchParams(props.location.search).get(
                                                'from'
                                            ) || undefined
                                        }
                                    />
                                )}
                            />
                            <AuthenticatedRoute
                                // TODO: remove /spa together with <ProjectsRedirect />
                                path={['/spa/projects/:projectId', '/projects/:projectId']}
                                component={ProjectsApp}
                            />
                            <Route
                                exact
                                path="/spa/status"
                                render={() => <Status fallback={<Loading />} />}
                            />
                            {!onboardingExperimentActive && (
                                <Route
                                    path="/account/application"
                                    render={() => <GettingStarted fallback={<Loading />} />}
                                />
                            )}
                            <AuthenticatedRoute
                                path="/account"
                                render={() => <Account fallback={<Loading />} />}
                            />
                            <AuthenticatedRoute
                                path={[
                                    '/spa/handover/:handoverId/messages',
                                    '/handover/:handoverId/messages',
                                ]}
                                component={HandoverMessages}
                            />
                            <Route render={() => <FullscreenError errorCode="404" />} />
                        </Switch>
                    </Layout>
                )}
            />
        </Switch>
    );
}

function primaryRole(user: ViewerContext_UserFragment | null): Role {
    if (!user) {
        return 'logged-out';
    } else if (user.isAdmin) {
        return 'contest-holder';
    } else if (user.isDesigner) {
        return 'designer';
    } else {
        return 'contest-holder';
    }
}
